import { useEffect, useState } from 'react'
import throttle from 'lodash/throttle'

import { Breakpoints } from '../theme/Responsive'

export default function useClientInfo() {
  const [clientInfo, setClientInfo] = useState(
    new ClientInfo(window.innerWidth, window.innerHeight),
  )

  useEffect(() => {
    const onResize = throttle(() => {
      setClientInfo(new ClientInfo(window.innerWidth, window.innerHeight))
    }, 300)
    window.addEventListener('resize', onResize, false)
    return () => {
      window.removeEventListener('resize', onResize, false)
    }
  }, [])

  return clientInfo
}

// Copied from https://github.com/taskworld/tw-frontend/blob/4596fec41e2756a9b12d43d01845508c7a78e724/client/src/tw-client-info/ClientInfo.js
class ClientInfo {
  constructor(private width: number, private height: number) {}

  // Returns `true` if the client has the size of a mobile device.
  isMobile() {
    return this.width <= Breakpoints.Mobile
  }

  // Returns `true` if the client has the size of a mobile device.
  isPortraitTablet() {
    return this.width > Breakpoints.Mobile && this.width <= Breakpoints.TabletPortrait
  }

  // Returns `true` if the client has the size of a landscape tablet.
  isLandscapeTablet() {
    return this.width > Breakpoints.TabletPortrait && this.width <= Breakpoints.TabletLandscape
  }

  // Returns `true` if the client has the size of a desktop.
  isDesktop() {
    return this.width > Breakpoints.TabletLandscape
  }

  // Returns the viewport width.
  // Usage note: Please avoid passing this directly to components, as
  //             this will cause the component to re-render each page resize.
  getViewportWidth() {
    return this.width
  }

  // Returns the viewport height.
  // Usage note: Please avoid passing this directly to components, as
  //             this will cause the component to re-render each page resize.
  getViewportHeight() {
    return this.height
  }

  getDeviceName() {
    if (/Android/i.test(navigator.userAgent)) {
      return 'android'
    }

    if (/iPhone|iPad|iPod/i.test(navigator.userAgent)) {
      return 'ios'
    }

    return null
  }

  // Returns `true` if the client is run in Cordova.
  // Usage note: Please avoid using this unless absolutely necessary.
  //             The end goal is to remove this method and use feature-
  //             detection instead.
  isCordova(): boolean {
    throw new Error('Not implemented')
    // return TW_FRONTEND_APP_CONFIG.runtimeEnvironment === 'cordova'
  }

  isPWA() {
    return window.matchMedia ? window.matchMedia('(display-mode: standalone)').matches : false
  }

  isAndroid() {
    return this.getDeviceName() === 'android'
  }

  isIOS() {
    return this.getDeviceName() === 'ios'
  }

  isMobilePlatform() {
    return this.isAndroid() || this.isIOS()
  }

  isIE() {
    // MSIE for IE version 1-10, Trident for IE 11 to above
    return /MSIE/i.test(navigator.userAgent) || /Trident/i.test(navigator.userAgent)
  }

  isSafari() {
    // Please beware of chrome browser in iOS
    // That's why this method is complicated
    return (
      !!navigator.vendor &&
      navigator.vendor.includes('Apple') &&
      !!navigator.userAgent &&
      !navigator.userAgent.match('CriOS')
    )
  }

  isIpad() {
    return this.isIOS() && (this.isPortraitTablet() || this.isLandscapeTablet())
  }

  isTabletApp() {
    return this.isCordova() && (this.isPortraitTablet() || this.isLandscapeTablet())
  }
}
